<script setup>
import { layoutConfig } from '@layouts'
import {
    VerticalNavGroup,
    VerticalNavLink,
    VerticalNavSectionTitle,
} from '@layouts/components'
import { useLayoutConfigStore } from '@layouts/stores/config'
import { injectionKeyIsVerticalNavHovered } from '@layouts/symbols'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import { VNodeRenderer } from './VNodeRenderer'
import { useUserStore } from '@/stores/user'
import { useRouter } from 'vue-router'
import { useCookie } from '@/@core/composable/useCookie'
import { useAbility } from '@/plugins/casl/composables/useAbility'
import { useElementHover } from '@vueuse/core'
import { ref, computed, watch } from 'vue'
import { useSessionTimeout } from '@/composables/core/useSessionTimeout'
import AspBtn from '@/@core/components/AspBtn.vue'

const props = defineProps({
    tag: {
        type: null,
        required: false,
        default: 'aside',
    },
    navItems: {
        type: null,
        required: true,
    },
    isOverlayNavActive: {
        type: Boolean,
        required: true,
    },
    toggleIsOverlayNavActive: {
        type: Function,
        required: true,
    },
    nextSyncTime: {
        type: String,
        default: '',
    },
    isSidebarCollapsed: {
        type: Boolean,
        default: false,
    },
    onToggleSidebar: {
        type: Function,
        default: null,
    },
})

const router = useRouter()
const ability = useAbility()
const userStore = useUserStore()
const { logout: sessionLogout } = useSessionTimeout()

// TODO: Get type from backend
// userData is stored in localStorage (too large for cookie)
const userData = ref(localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')) : null)
const selectedBrand = useCookie('selectedBrand')
const selectedCountry = useCookie('selectedCountry')

const refNav = ref()
const isHovered = useElementHover(refNav)

provide(injectionKeyIsVerticalNavHovered, isHovered)

const configStore = useLayoutConfigStore()

// State for pinning the nav - always pinned and expanded
const isNavPinned = ref(true)

// Force nav to always be expanded
configStore.isVerticalNavCollapsed = false

// Prevent any collapse behavior
watch(isHovered, (hovered) => {
    // Always keep expanded
    configStore.isVerticalNavCollapsed = false
})

// Sync pinned state with sidebar collapsed — the :has(.pinned) CSS rule controls
// content padding, so we must remove the pinned class when the sidebar hides.
watch(() => props.isSidebarCollapsed, (collapsed) => {
    isNavPinned.value = !collapsed
})

// Persistent sidebar: if the overlay nav somehow gets activated, immediately close it.
// This prevents the dark overlay from appearing when scrolling the sidebar.
watch(() => props.isOverlayNavActive, (active) => {
    if (active) props.toggleIsOverlayNavActive(false)
})

// Toggle pin state
const toggleNavPin = () => {
    isNavPinned.value = !isNavPinned.value

    if (isNavPinned.value) {
        // When pinned, expand the nav
        configStore.isVerticalNavCollapsed = false
    } else {
        // When unpinned, collapse if not hovered
        configStore.isVerticalNavCollapsed = !isHovered.value
    }
}

const resolveNavItemComponent = (item) => {
    if ('heading' in item) {
        if (item.heading === 'divider') {
            return 'div'
        }
        return VerticalNavSectionTitle
    }
    if ('children' in item) return VerticalNavGroup

    return VerticalNavLink
}

/*ℹ️ Close overlay side when route is changed
Close overlay vertical nav when link is clicked
*/
const route = useRoute()

watch(
    () => route.name,
    () => {
        props.toggleIsOverlayNavActive(false)
    }
)

const updateIsVerticalNavScrolled = (_val) => {}

const hideTitleAndIcon = false

const logout = async () => {
    try {
        // Use the session timeout system's logout function which properly handles audit API push
        await sessionLogout('manual')
    } catch (error) {
        console.error('Error during logout:', error)
        // Even if there's an error, try to redirect to login
        await router.push({ name: 'login' })
    }
}
</script>

<template>
    <Component
        :is="props.tag"
        ref="refNav"
        class="layout-vertical-nav"
        :class="[
            {
                'overlay-nav': configStore.isLessThanOverlayNavBreakpoint,
                hovered: isHovered,
                visible: isOverlayNavActive,
                pinned: isNavPinned,
            },
        ]"
    >
        <!-- 👉 Header -->
        <div class="nav-header" style="margin-bottom: 20%">
            <slot name="nav-header">
                <div
                    class="nav-header-content"
                    :class="{ 'is-collapsed': hideTitleAndIcon }"
                >
                    <RouterLink to="/" class="app-logo">
                        <!-- Show "A" logo when collapsed/mini, full logo when expanded -->
                        <img
                            v-if="hideTitleAndIcon"
                            src="@/assets/images/aspial_logo_a_orig.png"
                            alt="Aspial A"
                            class="app-logo-a"
                        />
                        <img
                            v-else
                            src="@/assets/images/aspial_logo.png"
                            alt="Aspial"
                            class="app-logo-orig"
                        />
                    </RouterLink>
                </div>
            </slot>
        </div>
        <slot name="before-nav-items">
            <div class="vertical-nav-items-shadow" />
        </slot>
        <slot
            name="nav-items"
            :update-is-vertical-nav-scrolled="updateIsVerticalNavScrolled"
        >
            <PerfectScrollbar
                :key="configStore.isAppRTL"
                tag="ul"
                class="nav-items"
                :options="{ wheelPropagation: false }"
            >
                <Component
                    :is="resolveNavItemComponent(item)"
                    v-for="(item, index) in navItems"
                    :key="index"
                    :item="item"
                />
            </PerfectScrollbar>
        </slot>

        <slot name="after-nav-items" />

        <!-- Footer container for Next Sync and Logout -->
        <div class="nav-footer">
            <!-- Next Sync text - always shown, different layout based on nav state -->
            <!-- <div
                class="text-center mb-4"
                :class="hideTitleAndIcon ? 'ml-1 mr-0' : 'mr-4'"
            >
                <div v-if="hideTitleAndIcon" class="sync-collapsed">
                    <div class="font-12 weight-400 text-wood-charcoal">
                        Next Sync:
                    </div>
                    <div class="font-12 weight-400 text-wood-charcoal">
                        {{ props.nextSyncTime || '...' }}
                    </div>
                </div>
                <span v-else class="font-13 weight-400 text-wood-charcoal"
                    >Next Sync: {{ props.nextSyncTime || '...' }}</span
                >
            </div> -->

            <!-- Logout button - icon only when collapsed, full button when expanded -->
            <AspBtn
                color="error"
                class="mb-4 mx-4 logout-btn btn-red logout-btn-with-hover-icon weight-400"
                simulate-loading
                size="large"
                title="Logout"
                @click="logout"
            >
                <template v-if="hideTitleAndIcon">
                    <VIcon
                        icon="ucsi-logout"
                        size="20"
                        class="logout-icon-collapsed"
                    />
                </template>
                <template v-else>
                    Logout
                    <span class="logout-icon-wrapper">
                        <VIcon icon="ucsi-logout-box" size="20" />
                        <VIcon
                            class="logout-arrow"
                            icon="ucsi-logout-arrow"
                            size="12"
                        />
                    </span>
                </template>
            </AspBtn>
        </div>
    </Component>
</template>

<style lang="scss" scoped>
.app-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 0.75rem;

    .app-logo-a {
        height: 32px;
        width: auto;
    }

    .app-logo-orig {
        height: 33px;
        width: auto;
    }

    .app-logo-text {
        font-size: 2rem;
        font-weight: 700;
        line-height: 1;
        color: rgb(var(--v-theme-primary));
        text-decoration: none;
    }

    .app-logo-title {
        font-size: 1.25rem;
        font-weight: 600;
        line-height: 1.75rem;
        text-transform: uppercase;
    }
}

.nav-header {
    padding: 1.25rem 1rem;

    .nav-header-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        // When collapsed, center the logo
        &.is-collapsed {
            justify-content: center;
        }
    }

    .app-logo {
        min-width: 0;
    }

    .nav-toggle-btn {
        flex-shrink: 0;
        margin-inline-start: auto;
    }
}

// Logout button styles
.logout-icon-collapsed {
    margin-left: 2px;
}

.logout-arrow {
    margin-top: -2px !important;
    margin-left: -11px !important;
    transition: transform 0.2s ease-in-out;
}

.logout-btn-with-hover-icon {
    width: calc(100% - 2rem); // Account for mx-4 (1rem on each side)
    display: flex;
    justify-content: center;

    .logout-icon-wrapper {
        display: inline-flex;
        position: relative;
        width: 30px;
        height: 20px;
        align-items: center;
        margin-left: 8px;
    }

    &:hover:not(.v-btn--disabled) {
        .logout-arrow {
            transform: translateX(7px);
        }
    }
}

// Nav toggle button pinned state
.nav-toggle-btn {
    transition: transform 0.2s ease-in-out;
}
</style>

<style lang="scss">
@use '@configured-variables' as variables;
@use '@layouts/styles/mixins';

// 👉 Vertical Nav
.layout-vertical-nav {
    position: fixed;
    z-index: variables.$layout-vertical-nav-z-index;
    display: flex;
    flex-direction: column;
    block-size: 100%;
    inline-size: variables.$layout-vertical-nav-width;
    inset-block-start: 0;
    inset-inline-start: 0;
    transition: inline-size 0.25s ease-in-out, box-shadow 0.25s ease-in-out;
    will-change: transform, inline-size;

    .nav-header {
        display: flex;
        align-items: center;

        .header-action {
            cursor: pointer;

            @at-root {
                #{variables.$selector-vertical-nav-mini}
                    .nav-header
                    .header-action {
                    &.nav-pin,
                    &.nav-unpin {
                        display: none !important;
                    }
                }
            }
        }
    }

    .app-title-wrapper {
        margin-inline-end: auto;
    }

    .nav-items {
        block-size: 100%;

        // Custom divider styling
        > div:not(.nav-link):not(.nav-group) {
            height: 1px;
            width: 95%;
            background-color: rgba(0, 0, 0, 0.12);
            margin: 8px 0;
            margin-left: 0;
        }
    }

    .nav-footer {
        margin-top: auto;
        flex-shrink: 0;
        padding-block-end: 0.5rem;
    }

    .nav-item-title {
        overflow: hidden;
        margin-inline-end: auto;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    // 👉 Collapsed - don't collapse when pinned
    .layout-vertical-nav-collapsed & {
        &:not(.hovered):not(.pinned) {
            inline-size: variables.$layout-vertical-nav-collapsed-width;
        }
    }

    // 👉 Pinned state - keep expanded
    &.pinned {
        inline-size: variables.$layout-vertical-nav-width;
    }
}

// Adjust content wrapper when nav is pinned
.layout-wrapper {
    .layout-content-wrapper {
        transition: padding-inline-start 0.25s ease-in-out;
    }

    // When nav is pinned, adjust content wrapper padding
    &:has(.layout-vertical-nav.pinned) {
        .layout-content-wrapper {
            padding-inline-start: variables.$layout-vertical-nav-width !important;
        }
    }
}

// Small screen vertical nav transition
@media (max-width: 1279px) {
    .layout-vertical-nav {
        &:not(.visible) {
            transform: translateX(-#{variables.$layout-vertical-nav-width});

            @include mixins.rtl {
                transform: translateX(variables.$layout-vertical-nav-width);
            }
        }

        transition: transform 0.25s ease-in-out;
    }
}
</style>
